import { Box, Button, FormField, Input, Link, StatusIndicator, TreeView } from '@amzn/awsui-components-core';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from 'src/components/common/LoadingSpinner';
import { ProductLineSelect } from 'src/components/common/ProductLineSelect';
import { usePersistedState } from 'src/hooks/useLocalStorage';
import { usePermissions } from 'src/hooks/usePermissions';
import { useAppSelector } from 'src/store/hooks';
import {
  selectActiveProductLineOptionsFromMinimalHierarchy,
  selectProductHierarchyMinimalHasSuccessfulFetch,
} from 'src/store/selectors/productHierarchySelectors';
import { selectFpnFavoriteIds, selectRpnFavoriteIds } from 'src/store/selectors/userPreferencesSelectors';
import { FPNMinimalNode, RPNMinimalNode } from 'src/types/productLines/productHierarchy.types';
import { navigatePreservingParams } from 'src/utils/navigationUtils';
import { useProductRpnFpnHierarchyContext } from './hooks/useProductRpnFpnHierarchyContext';
import { resolveAnchorHashForTarget } from './rpn-fpn/config/rpnFpnSections.config';
import './styles/ManageProgramsSideNavigationV2.scss';
import { getLastEditedFPNs } from './utils/lastEditedItemsUtils';
import { buildFpnPath, buildProductLinePath, buildRpnPath } from './utils/manageProgramsPathUtils';

/**
 * TreeView item type for RPN/FPN hierarchy
 */
interface TreeItem {
  id: string;
  label: string;
  href: string;
  type: 'rpn' | 'fpn' | 'placeholder';
  isPlaceholder?: boolean;
  children?: TreeItem[];
}

/**
 * List item type for last edited programs
 */
interface LastEditedItem {
  id: string;
  label: string;
  href: string;
}

/**
 * Custom Divider component for visual separation
 */
const Divider: React.FC = () => <div className="side-nav-v2-divider" />;

/**
 * ProductLineRpnFpnSideNavigationV2 is a custom side navigation component
 * that replaces the Cloudscape SideNavigation component.
 *
 * This component uses TreeView for hierarchical navigation,
 * List for last edited programs, and custom styling.
 */
const ProductLineRpnFpnSideNavigationV2: React.FC = () => {
  const [activeHref, setActiveHref] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [expandAll, setExpandAll] = usePersistedState('manage-programs-expand-all', false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const { productLineId, productLineHierarchyMetadata } = useProductRpnFpnHierarchyContext();
  const productLineOptions = useAppSelector(selectActiveProductLineOptionsFromMinimalHierarchy);
  const rpnFavoriteIds = useAppSelector(selectRpnFavoriteIds);
  const fpnFavoriteIds = useAppSelector(selectFpnFavoriteIds);
  const { canAccessConfidentialRPN, isLoading: isPermissionsLoading } = usePermissions();
  const treeContainerRef = useRef<HTMLDivElement>(null);

  // Check if minimal hierarchy is loaded (needed for tree data)
  const hasMinimalHierarchyLoaded = useAppSelector(selectProductHierarchyMinimalHasSuccessfulFetch);

  useEffect(() => {
    setActiveHref(pathname);
  }, [pathname]);

  // Auto-scroll to active item only when activeHref changes (i.e., user navigates)
  const lastScrolledHref = useRef<string>('');
  useEffect(() => {
    if (!activeHref || !treeContainerRef.current || activeHref === lastScrolledHref.current) return;

    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(() => {
      const activeElement = treeContainerRef.current?.querySelector('[data-active="true"]');
      if (activeElement) {
        lastScrolledHref.current = activeHref;
        activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [activeHref]);

  // Build tree items from hierarchy metadata
  // Filter out confidential RPNs user doesn't have access to
  const treeItems = useMemo((): TreeItem[] => {
    if (!productLineHierarchyMetadata || !productLineId || isPermissionsLoading) return [];

    const productCode = productLineHierarchyMetadata.product_code;

    // Filter RPNs based ONLY on confidential access
    // All users can see all product lines by default - only confidential RPNs need special access
    const accessibleRpns =
      productLineHierarchyMetadata.children?.filter((rpn: RPNMinimalNode) => {
        // Check if user can access this (potentially confidential) RPN
        const canAccess = canAccessConfidentialRPN(Number(productLineId), rpn.rpn_id);
        return canAccess;
      }) || [];

    const items =
      accessibleRpns.map((rpn: RPNMinimalNode) => {
        const fpnChildren =
          rpn.children
            ?.map((fpn: FPNMinimalNode) => ({
              id: `fpn-${fpn.fpn_id}`,
              label: fpn.fpn_alias,
              href: buildFpnPath(productCode, rpn.rpn_display_id, fpn.fpn_display_id),
              type: 'fpn' as const,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) || [];

        // Add placeholder child if RPN has no FPNs
        const children: TreeItem[] =
          fpnChildren.length > 0
            ? fpnChildren
            : [
                {
                  id: `placeholder-${rpn.rpn_display_id}`,
                  label: 'No FPNs',
                  href: '',
                  type: 'placeholder' as const,
                  isPlaceholder: true,
                },
              ];

        return {
          id: `rpn-${rpn.rpn_display_id}`,
          label: rpn.rpn_alias,
          href: buildRpnPath(productCode, rpn.rpn_display_id),
          type: 'rpn' as const,
          children,
        };
      }) || [];

    // Sort top-level items alphabetically
    return items.sort((a, b) => a.label.localeCompare(b.label));
  }, [productLineId, productLineHierarchyMetadata, canAccessConfidentialRPN, isPermissionsLoading]);

  // Filter tree items based on search query
  const filteredTreeItems = useMemo((): TreeItem[] => {
    if (!searchQuery.trim()) return treeItems;

    const lowerQuery = searchQuery.toLowerCase().trim();

    const filtered: TreeItem[] = [];

    treeItems.forEach((rpn) => {
      // Check if RPN label matches
      const rpnMatches = rpn.label.toLowerCase().includes(lowerQuery);

      // Filter children (FPNs) that match
      const matchingChildren = rpn.children?.filter((fpn) => fpn.label.toLowerCase().includes(lowerQuery)) || [];

      // Include RPN if it matches or has matching children
      if (rpnMatches || matchingChildren.length > 0) {
        filtered.push({
          ...rpn,
          // If RPN matches, show all children; otherwise, show only matching children
          children: rpnMatches ? rpn.children : matchingChildren,
        });
      }
    });

    return filtered;
  }, [treeItems, searchQuery]);

  // Auto-expand all items when searching
  useEffect(() => {
    if (searchQuery.trim()) {
      setExpandedItems(filteredTreeItems.map((item) => item.id));
    }
  }, [searchQuery, filteredTreeItems]);

  // Calculate initial expanded items based on active href
  useEffect(() => {
    // Skip if there's an active search query (search handles its own expansion)
    if (searchQuery.trim()) return;

    if (expandAll) {
      // Expand all RPNs
      setExpandedItems(treeItems.map((item) => item.id));
    } else if (activeHref.includes('/fpn/')) {
      // FPN level: Find and expand the parent RPN
      const parentRpn = treeItems.find((rpn) => rpn.children?.some((fpn) => fpn.href === activeHref));
      if (parentRpn) {
        setExpandedItems([parentRpn.id]);
      }
    } else if (activeHref.includes('/rpn/')) {
      // RPN level: Expand the selected RPN
      const selectedRpn = treeItems.find((rpn) => rpn.href === activeHref);
      if (selectedRpn) {
        setExpandedItems([selectedRpn.id]);
      }
    } else if (treeItems.length > 0) {
      // Product Line level: Expand first RPN
      setExpandedItems([treeItems[0].id]);
    }
  }, [activeHref, expandAll, treeItems, searchQuery]);

  // Handle tree item toggle
  const handleItemToggle = useCallback(({ detail }: { detail: { id: string; expanded: boolean } }) => {
    setExpandedItems((prev) => {
      if (detail.expanded) {
        return [...prev, detail.id];
      } else {
        return prev.filter((id) => id !== detail.id);
      }
    });
  }, []);

  // Handle navigation - preserves the current anchor section hash
  const handleNavigate = useCallback(
    (href: string) => {
      const currentHash = window.location.hash;
      const isRpnOrFpnTarget = href.includes('/rpn/') || href.includes('/fpn/');
      if (currentHash && isRpnOrFpnTarget) {
        const targetType = href.includes('/fpn/') ? 'fpn' : 'rpn';
        const resolvedHash = resolveAnchorHashForTarget(currentHash, targetType);
        navigatePreservingParams(navigate, href + resolvedHash);
      } else {
        navigatePreservingParams(navigate, href);
      }
    },
    [navigate]
  );

  // Build last edited FPNs list using shared utility, scoped to the current product line
  // Filter out FPNs under confidential RPNs the user cannot access
  const lastEditedItems = useMemo((): LastEditedItem[] => {
    if (!productLineHierarchyMetadata || !productLineId || isPermissionsLoading) return [];

    const productCode = productLineHierarchyMetadata.product_code;
    const allLastEditedFpns = getLastEditedFPNs([productLineHierarchyMetadata]);

    return allLastEditedFpns
      .filter(({ rpn }) => canAccessConfidentialRPN(Number(productLineId), rpn.rpn_id))
      .slice(0, 10)
      .map(({ fpn, rpn }) => ({
        id: `recent-fpn-${fpn.fpn_id}`,
        label: fpn.fpn_alias,
        href: buildFpnPath(productCode, rpn.rpn_display_id, fpn.fpn_display_id),
      }));
  }, [productLineId, productLineHierarchyMetadata, canAccessConfidentialRPN, isPermissionsLoading]);

  // Build favorite items list filtered by current product line
  // Filter out items under confidential RPNs the user cannot access
  const favoriteItems = useMemo((): LastEditedItem[] => {
    if (!productLineHierarchyMetadata || !productLineId || isPermissionsLoading) return [];

    const productCode = productLineHierarchyMetadata.product_code;
    const rpnFavoriteSet = new Set(rpnFavoriteIds);
    const fpnFavoriteSet = new Set(fpnFavoriteIds);
    const items: LastEditedItem[] = [];

    // Iterate through hierarchy to find favorited RPNs and FPNs
    productLineHierarchyMetadata.children?.forEach((rpn: RPNMinimalNode) => {
      // Check confidential access for this RPN
      const hasAccess = canAccessConfidentialRPN(Number(productLineId), rpn.rpn_id);
      if (!hasAccess) return;

      // Check if RPN is favorited
      if (rpnFavoriteSet.has(rpn.rpn_id)) {
        items.push({
          id: `favorite-rpn-${rpn.rpn_id}`,
          label: `${rpn.rpn_alias} (RPN)`,
          href: buildRpnPath(productCode, rpn.rpn_display_id),
        });
      }

      // Check FPNs under this RPN
      rpn.children?.forEach((fpn: FPNMinimalNode) => {
        if (fpnFavoriteSet.has(fpn.fpn_id)) {
          items.push({
            id: `favorite-fpn-${fpn.fpn_id}`,
            label: `${fpn.fpn_alias} (FPN)`,
            href: buildFpnPath(productCode, rpn.rpn_display_id, fpn.fpn_display_id),
          });
        }
      });
    });

    // Sort alphabetically by label
    return items.sort((a, b) => a.label.localeCompare(b.label));
  }, [
    productLineId,
    productLineHierarchyMetadata,
    rpnFavoriteIds,
    fpnFavoriteIds,
    canAccessConfidentialRPN,
    isPermissionsLoading,
  ]);

  // Show loading spinner until base data is available
  // Need both product line options AND minimal hierarchy data
  const isBaseDataLoaded = productLineOptions.length > 0 && hasMinimalHierarchyLoaded;

  // Only show loading spinner on initial load, not on background refetches
  if (!hasMinimalHierarchyLoaded) {
    return <LoadingSpinner />;
  }

  // Show loading spinner until product line options are ready
  if (!isBaseDataLoaded) {
    return <LoadingSpinner />;
  }

  // Detect invalid product code ONLY after base data has loaded
  // If productLineId is in URL but no matching metadata found, it's invalid
  const hasInvalidProductCode = productLineId && !productLineHierarchyMetadata;

  // Show error state if product code is invalid (only after data is loaded)
  if (hasInvalidProductCode) {
    return (
      <div className="side-nav-v2-container" data-testid="side-navigation-v2">
        <div className="side-nav-v2-header">
          {/* Product Line Select Dropdown  */}
          <ProductLineSelect
            className="side-nav-v2-select"
            selectedOption={null}
            grouped={true}
            showRpnCount={true}
            onChange={(option) => {
              const selectedProductCode = option?.description;
              if (selectedProductCode) {
                handleNavigate(buildProductLinePath(selectedProductCode));
              }
            }}
            expandToViewport={true}
          />
        </div>
        <Divider />
        <Box padding="l" textAlign="center">
          <StatusIndicator type="warning">Product code not found</StatusIndicator>
          <Box variant="p" color="text-body-secondary" margin={{ top: 's' }}>
            Select a product line from the dropdown to view programs
          </Box>
        </Box>
      </div>
    );
  }

  // Success state - render full navigation
  return (
    <div className="side-nav-v2-container" data-testid="side-navigation-v2">
      {/* Header - Product Line Selector */}
      <div className="side-nav-v2-header">
        <ProductLineSelect
          className="side-nav-v2-select"
          selectedOption={
            productLineId ? productLineOptions.find((opt) => opt.value === `${productLineId}`) || null : null
          }
          grouped={true}
          showRpnCount={true}
          onChange={(option) => {
            // Use description field which contains product_code (value contains product_line_id)
            const selectedProductCode = option?.description;
            if (selectedProductCode) {
              handleNavigate(buildProductLinePath(selectedProductCode));
            }
          }}
          expandToViewport={true}
        />
      </div>

      <Divider />

      {/* Sections wrapper - enables percentage-based height distribution */}
      <div className="side-nav-v2-content">
        {/* Section 1: All Programs (TreeView) - 55% */}
        <div className="side-nav-v2-section side-nav-v2-section--programs">
          <div className="side-nav-v2-section-header-with-toggle">
            <FormField className="width-13-rem">
              <Input
                type="search"
                placeholder="Search programs..."
                value={searchQuery}
                onChange={({ detail }) => setSearchQuery(detail.value)}
                ariaLabel="Search programs"
                clearAriaLabel="Clear search"
              />
            </FormField>
            <Button
              variant="icon"
              iconName={expandAll ? 'treeview-collapse' : 'treeview-expand'}
              onClick={() => setExpandAll(!expandAll)}
              ariaLabel={expandAll ? 'Collapse all programs' : 'Expand all programs'}
            />
          </div>
          <div className="side-nav-v2-scrollable-content" ref={treeContainerRef}>
            {filteredTreeItems.length > 0 ? (
              <TreeView<TreeItem>
                items={filteredTreeItems}
                expandedItems={expandedItems}
                onItemToggle={handleItemToggle}
                connectorLines="none"
                ariaLabel="Program hierarchy navigation"
                getItemId={(item) => item.id}
                getItemChildren={(item) => item.children || []}
                renderItem={(item) => ({
                  content: item.isPlaceholder ? (
                    <span className="side-nav-v2-placeholder">{item.label}</span>
                  ) : (
                    <span
                      className={activeHref === item.href ? 'side-nav-v2-link-active' : 'side-nav-v2-link-inactive'}
                      data-active={activeHref === item.href}
                    >
                      <Link
                        href={item.href}
                        onFollow={(e) => {
                          e.preventDefault();
                          handleNavigate(item.href);
                        }}
                      >
                        {item.label}
                      </Link>
                    </span>
                  ),
                })}
              />
            ) : (
              <Box variant="p" color="text-body-secondary" padding={{ left: 's' }}>
                {searchQuery.trim() ? 'No matching programs found' : 'No programs available'}
              </Box>
            )}
          </div>
        </div>

        <Divider />

        {/* Section 2: Last Edited Programs - 22% */}
        <div className="side-nav-v2-section side-nav-v2-section--recent">
          <div className="side-nav-v2-section-header">
            <Box variant="h5" color="text-label">
              Last Edited FPNs
            </Box>
          </div>
          <div className="side-nav-v2-scrollable-content">
            {lastEditedItems.length > 0 ? (
              <ul className="side-nav-v2-bullet-list" aria-label="Last Edited FPNs">
                {lastEditedItems.map((item) => (
                  <li key={item.id}>
                    <span
                      className={activeHref === item.href ? 'side-nav-v2-link-active' : 'side-nav-v2-link-inactive'}
                    >
                      <Link
                        href={item.href}
                        onFollow={(e) => {
                          e.preventDefault();
                          handleNavigate(item.href);
                        }}
                      >
                        {item.label}
                      </Link>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <Box variant="p" color="text-body-secondary" padding={{ left: 's' }}>
                No recent edits
              </Box>
            )}
          </div>
        </div>

        <Divider />

        {/* Section 3: My Favorites - 23% */}
        <div className="side-nav-v2-section side-nav-v2-section--favorites">
          <div className="side-nav-v2-section-header">
            <Box variant="h5" color="text-label">
              My Favorites
            </Box>
          </div>
          <div className="side-nav-v2-scrollable-content">
            {favoriteItems.length > 0 ? (
              <ul className="side-nav-v2-bullet-list" aria-label="My favorite programs">
                {favoriteItems.map((item) => (
                  <li key={item.id}>
                    <span
                      className={activeHref === item.href ? 'side-nav-v2-link-active' : 'side-nav-v2-link-inactive'}
                    >
                      <Link
                        href={item.href}
                        onFollow={(e) => {
                          e.preventDefault();
                          handleNavigate(item.href);
                        }}
                      >
                        {item.label}
                      </Link>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <Box variant="p" color="text-body-secondary" padding={{ left: 's' }}>
                No favorites yet
              </Box>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductLineRpnFpnSideNavigationV2;
